/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Fragment } from 'preact';
import { classNames } from '../utils/UNSAFE_classNames';
import { Flex } from '../UNSAFE_Flex';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '../resources/nls/bundle';
import { ItemContext } from 'src/UNSAFE_Collection';
import { forwardRef, ForwardedRef } from 'preact/compat';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { IndexerStyles, IndexerItemVariantOptions } from './themes/IndexerStyles.css';
import { IndexerRedwoodTheme } from './themes/redwood/IndexerTheme';

/**
 * Type of a section
 */
export type Section<K> = {
  /**
   * The key of each section
   */
  key: K;
  /**
   * The label of each section
   */
  label?: string;
  /**
   * A property that indicates whether the section
   * is disabled or not. Typically, when Indexer is
   * used with ListView, a disabled section implies
   * that there's no corresponding items in the ListView
   */
  isDisabled?: boolean;
};

/**
 * The others section variable that application could use
 * to compare value
 */
// const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
export const SECTION_OTHERS: Section<string> = Object.freeze({
  key: '__others__',
  label: '#'
});

const ITEM_STYLE_CLASS = 'oj-indexer-item';
export const ITEM_SELECTOR = '.' + ITEM_STYLE_CLASS;

export type SectionProps<K> = {
  context: ItemContext<Section<K>>;
  currentKey: K;
  selectedKey: K;
  valueNow: number; // the index of an item within currently visible sections
  isFocusRingVisible: boolean;
};

export const SectionItem = forwardRef(
  <K extends string | number>(props: SectionProps<K>, ref: ForwardedRef<HTMLLIElement>) => {
    const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

    const { context, selectedKey } = props;
    const isSelected = context.data.key === selectedKey;
    const commonProps = getCommonProps(props, ref);

    const { classes } = useComponentTheme<IndexerItemVariantOptions, IndexerStyles>(
      IndexerRedwoodTheme,
      {
        focused: commonProps.ref && commonProps.isFocusRingVisible ? 'isFocused' : 'notFocused',
        disabled: context.data.isDisabled ? 'isDisabled' : 'notDisabled',
        selected: isSelected ? 'isSelected' : 'notSelected'
      }
    );
    const itemClasses = classNames([commonProps.class, classes]);

    const sectionLabel = context.data.label || context.data.key;
    const valueText = context.data.isDisabled
      ? translations.indexer_disabledLabel({ SECTION: `${sectionLabel}` })
      : `${sectionLabel}`;

    return (
      <li
        {...commonProps}
        class={itemClasses}
        data-oj-valuetext={valueText}
        data-oj-disabled={context.data.isDisabled}>
        {context.data.label || context.data.key}
      </li>
    );
  }
);

function getCommonProps<K>(
  { context, currentKey, valueNow, isFocusRingVisible }: SectionProps<K>,
  ref: ForwardedRef<HTMLLIElement>
) {
  const isCurrent = context.data.key === currentKey;
  return {
    ...(isCurrent && { ref }),
    key: context.data.key,
    class: ITEM_STYLE_CLASS,
    'data-oj-key': context.data.key,
    'data-oj-key-type': typeof context.data.key,
    'data-oj-valuenow': valueNow,
    isFocusRingVisible: !!isFocusRingVisible
  };
}

type LastSectionProps<K> = SectionProps<K> & {
  lastValueNow: number;
};

// render the last section, that contains the last character and others character
export const LastSectionItem = forwardRef(
  <K extends string | number>(
    { lastValueNow, ...sectionProps }: LastSectionProps<K>,
    ref: ForwardedRef<HTMLLIElement>
  ) => {
    const commonProps = { ...sectionProps, ref };
    return (
      <Fragment>
        <SectionItem {...commonProps} />
        <OthersItem
          {...commonProps}
          ref={ref}
          context={{ index: -1, data: SECTION_OTHERS } as ItemContext<Section<K>>}
          valueNow={lastValueNow}
        />
      </Fragment>
    );
  }
);

const OthersItem = forwardRef(<K,>(props: SectionProps<K>, ref: ForwardedRef<HTMLLIElement>) => {
  const { context, selectedKey } = props;
  const commonProps = getCommonProps(props, ref);

  const isSelected = context.data.key === selectedKey;
  const { classes } = useComponentTheme<IndexerItemVariantOptions>(IndexerRedwoodTheme, {
    focused: commonProps.ref && commonProps.isFocusRingVisible ? 'isFocused' : 'notFocused',
    selected: isSelected ? 'isSelected' : 'notSelected'
  });
  const othersClasses = classNames([commonProps.class, classes]);
  const othersValueText = SECTION_OTHERS.label;

  return (
    <li {...commonProps} class={othersClasses} data-oj-valuetext={othersValueText}>
      {context.data.label}
    </li>
  );
});

type SeparatorProps<K> = SectionProps<K> & {
  sectionsPerTruncation: number;
  last: number;
  sections: Section<K>[];
};

export const SeparatorItem = forwardRef(
  <K extends string | number>(props: SeparatorProps<K>, ref: ForwardedRef<HTMLLIElement>) => {
    const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

    const { context, sections } = props;
    const commonProps = getCommonProps(props, ref);
    const { from, to } = getSeparatorRange(props);

    if (!isInSeparatorMiddle(context.index, from, to)) {
      return null;
    }

    const fromText = sections[from].label || sections[from].key;
    const toText = sections[to].label || sections[to].key;

    const {
      classes,
      styles: { dotStyle }
    } = useComponentTheme<IndexerItemVariantOptions, IndexerStyles>(IndexerRedwoodTheme, {
      focused: commonProps.ref && commonProps.isFocusRingVisible ? 'isFocused' : 'notFocused'
    });
    const separatorClasses = classNames([commonProps.class, classes]);

    const separatorValueText = translations.indexer_separatorLabel({
      FROM_SECTION: `${fromText}`,
      TO_SECTION: `${toText}`
    });

    return (
      <li {...commonProps} class={separatorClasses} data-oj-valuetext={separatorValueText}>
        <Flex align="center" justify="center">
          <div class={dotStyle}></div>
        </Flex>
      </li>
    );
  }
);

export function hasSeparator<K>(props: SeparatorProps<K>) {
  const { from, to } = getSeparatorRange(props);
  return isInSeparatorMiddle(props.context.index, from, to);
}

function getSeparatorRange<K>({ context, sectionsPerTruncation, last }: SeparatorProps<K>) {
  const from = context.index - (context.index % sectionsPerTruncation) + 1;
  let to = from + sectionsPerTruncation - 2;
  to = to >= last ? last - 1 : to;

  return { from, to };
}

function isInSeparatorMiddle(index: number, from: number, to: number) {
  return index === Math.floor((from + to) / 2);
}
